import { Currency } from './currencies';
import { b2cImpactStats } from './impact-stats';

export const freePlanId = 'free';

export interface Plans {
  [key: string]: Plan;
}

export interface Plan {
  id: string;
  title: string;
  description: string;
  price: number;
  stats: PlanStats;
  multiplier: number;
}

export interface PlanStats {
  carbon: number;
  carbonAnnual?: number;
  trees: number;
  footprints?: number;
}

const personalStartedTierDesc = 'Take your first steps into climate action with this plan.';
const personalMiddleTierDesc = 'The perfect plan to help you give back to the planet.';
const personalTopTierDesc = 'Double your positive impact for the planet with this plan.';

export const individualPlansHigh = {
  personal: {
    title: 'Starter',
    description: personalStartedTierDesc,
    stats: b2cImpactStats.high.personal,
    multiplier: 1,
  },
  booster: {
    title: 'Essential',
    description: personalMiddleTierDesc,
    stats: b2cImpactStats.high.booster,
    multiplier: 2,
  },
  mega: {
    title: 'Ultimate',
    description: personalTopTierDesc,
    stats: b2cImpactStats.high.mega,
    multiplier: 4,
  },
};

export const individualPlansMedium = {
  personal: {
    title: 'Starter',
    description: personalStartedTierDesc,
    stats: b2cImpactStats.medium.personal,
    multiplier: 1,
  },
  booster: {
    title: 'Essential',
    description: personalMiddleTierDesc,
    stats: b2cImpactStats.medium.booster,
    multiplier: 2,
  },
  mega: {
    title: 'Ultimate',
    description: personalTopTierDesc,
    stats: b2cImpactStats.medium.mega,
    multiplier: 4,
  },
};

export const individualPlansGB: Plans = {
  'personal-v3-gbp': {
    id: 'personal-v3-gbp',
    price: 4.7,
    ...individualPlansMedium.personal,
  },
  'booster-v3-gbp': {
    id: 'booster-v3-gbp',
    price: 9.4,
    ...individualPlansMedium.booster,
    description: personalMiddleTierDesc,
  },
  'mega-v3-gbp': {
    id: 'mega-v3-gbp',
    price: 18.8,
    ...individualPlansMedium.mega,
  },
};

export const individualPlansUS: Plans = {
  'personal-v3-usd': {
    id: 'personal-v3-usd',
    price: 10.5,
    ...individualPlansHigh.personal,
  },
  'booster-v3-usd': {
    id: 'booster-v3-usd',
    price: 21,
    ...individualPlansHigh.booster,
    description: personalMiddleTierDesc,
  },
  'mega-v3-usd': {
    id: 'mega-v3-usd',
    price: 42,
    ...individualPlansHigh.mega,
  },
};

export const individualPlansUSDMedium: Plans = {
  'personal-v3-usd-medium': {
    id: 'personal-v3-usd-medium',
    price: 6.4,
    ...individualPlansMedium.personal,
  },
  'booster-v3-usd-medium': {
    id: 'booster-v3-usd-medium',
    price: 12.8,
    ...individualPlansMedium.booster,
  },
  'mega-v3-usd-medium': {
    id: 'mega-v3-usd-medium',
    price: 25.6,
    ...individualPlansMedium.mega,
  },
};

export const individualPlansAU: Plans = {
  'personal-v3-aud': {
    id: 'personal-v3-aud',
    price: 15.4,
    ...individualPlansHigh.personal,
  },
  'booster-v3-aud': {
    id: 'booster-v3-aud',
    price: 30.8,
    ...individualPlansHigh.booster,
    description: personalMiddleTierDesc,
  },
  'mega-v3-aud': {
    id: 'mega-v3-aud',
    price: 61.6,
    ...individualPlansHigh.mega,
  },
};

export const individualPlansCA: Plans = {
  'personal-v3-cad': {
    id: 'personal-v3-cad',
    price: 14.1,
    ...individualPlansHigh.personal,
  },
  'booster-v3-cad': {
    id: 'booster-v3-cad',
    price: 28.2,
    ...individualPlansHigh.booster,
    description: personalMiddleTierDesc,
  },
  'mega-v3-cad': {
    id: 'mega-v3-cad',
    price: 56.4,
    ...individualPlansHigh.mega,
  },
};

export const individualPlansEU: Plans = {
  'personal-v3-eur': {
    id: 'personal-v3-eur',
    price: 5.6,
    ...individualPlansMedium.personal,
  },
  'booster-v3-eur': {
    id: 'booster-v3-eur',
    price: 11.2,
    ...individualPlansMedium.booster,
    description: personalMiddleTierDesc,
  },
  'mega-v3-eur': {
    id: 'mega-v3-eur',
    price: 22.4,
    ...individualPlansMedium.mega,
  },
};

export const allIndividualPlans: Plans = {
  ...individualPlansGB,
  ...individualPlansUS,
  ...individualPlansUSDMedium,
  ...individualPlansCA,
  ...individualPlansAU,
  ...individualPlansEU,
};

//  Current Personal Plan Ids (per currency)
export const personalPlanIdGB = 'personal-v3-gbp';
export const boosterPlanIdGB = 'booster-v3-gbp';
export const megaPlanIdGB = 'mega-v3-gbp';

export const personalPlanIdUS = 'personal-v3-usd';
export const boosterPlanIdUS = 'booster-v3-usd';
export const megaPlanIdUS = 'mega-v3-usd';

export const personalPlanIdUSMedium = 'personal-v3-usd-medium';
export const boosterPlanIdUSMedium = 'booster-v3-usd-medium';
export const megaPlanIdUSMedium = 'mega-v3-usd-medium';

export const personalPlanIdEU = 'personal-v3-eur';
export const boosterPlanIdEU = 'booster-v3-eur';
export const megaPlanIdEU = 'mega-v3-eur';

export const personalPlanIdAU = 'personal-v3-aud';
export const boosterPlanIdAU = 'booster-v3-aud';
export const megaPlanIdAU = 'mega-v3-aud';

export const personalPlanIdCA = 'personal-v3-cad';
export const boosterPlanIdCA = 'booster-v3-cad';
export const megaPlanIdCA = 'mega-v3-cad';

export const allCurrentPersonalPlanIds = [
  personalPlanIdGB,
  boosterPlanIdGB,
  megaPlanIdGB,
  personalPlanIdUS,
  boosterPlanIdUS,
  megaPlanIdUS,
  personalPlanIdUSMedium,
  boosterPlanIdUSMedium,
  megaPlanIdUSMedium,
  personalPlanIdEU,
  boosterPlanIdEU,
  megaPlanIdEU,
  personalPlanIdAU,
  boosterPlanIdAU,
  megaPlanIdAU,
  personalPlanIdCA,
  boosterPlanIdCA,
  megaPlanIdCA,
];

export const familyPlanIdGB = 'family-v3-gbp';
export const familyPlanIdUS = 'family-v3-usd';
export const familyPlanIdAU = 'family-v3-aud';
export const familyPlanIdCA = 'family-v3-cad';
export const familyPlanIdEU = 'family-v3-eur';
export const familyPlanIdUSMedium = 'family-v3-usd-medium';
export const familyPlans = [
  familyPlanIdGB,
  familyPlanIdUS,
  familyPlanIdAU,
  familyPlanIdCA,
  familyPlanIdEU,
  familyPlanIdUSMedium,
];

export const allCurrentFamilyPlanIds = [
  familyPlanIdGB,
  familyPlanIdUS,
  familyPlanIdUSMedium,
  familyPlanIdEU,
  familyPlanIdAU,
  familyPlanIdCA,
];

export type FamilyPlanOptions = {
  adults: number;
  children: number;
  pets: number;
};

export const businessPlanIdGB = 'business-v3-gbp';
export const businessPlanIdUS = 'business-v3-usd';
export const businessPlanIdAU = 'business-v3-aud';
export const businessPlanIdCA = 'business-v3-cad';
export const businessPlanIdEU = 'business-v3-eur';
export const businessPlanIdUSDMedium = 'business-v3-usd-medium';
export const businessPlans = [
  businessPlanIdUS,
  businessPlanIdUSDMedium,
  businessPlanIdGB,
  businessPlanIdAU,
  businessPlanIdCA,
  businessPlanIdEU,
];

export const allCurrentBusinessPlanIds = [
  businessPlanIdGB,
  businessPlanIdUS,
  businessPlanIdUSDMedium,
  businessPlanIdEU,
  businessPlanIdAU,
  businessPlanIdCA,
];

export type BusinessPlanOptions = {
  tier1: number;
  tier2: number;
  tier3: number;
};

export const allCurrentPlanIds = [
  ...allCurrentPersonalPlanIds,
  ...allCurrentBusinessPlanIds,
  ...allCurrentFamilyPlanIds,
];

/**
 * Is the supplied planId a (current) personal plan ?
 * @param {string} planId
 * @returns {boolean}
 */
export const isCurrentPersonalPlan = (planId: string) => {
  return allCurrentPersonalPlanIds.includes(planId);
};

/**
 * Is the supplied planId a (current) business plan ?
 * @param {string} planId
 * @returns {boolean}
 */
export const isCurrentBusinessPlan = (planId: string) => {
  return allCurrentBusinessPlanIds.includes(planId);
};

/**
 * Is the supplied planId a (current) family plan ?
 * @param {string} planId
 * @returns {boolean}
 */
export const isCurrentFamilyPlan = (planId: string) => {
  return allCurrentFamilyPlanIds.includes(planId);
};

export const isPersonalPlan = (planId: string) => {
  return planId.includes('personal');
};

export const isBoosterPlan = (planId: string) => {
  return planId.includes('booster');
};

export const isMegaPlan = (planId: string) => {
  return planId.includes('mega');
};

export const isBusinessPlan = (planId: string) => {
  return planId.includes('business');
};

export const isFamilyPlan = (planId: string) => {
  return planId.includes('family');
};

export const isCurrentPlan = (planId: string) => {
  return isV3Subscriber(planId);
};

export const isUSMediumPlan = (planId: string) => {
  return planId.includes('medium');
};

export const isV1Subscriber = (planId: string) => {
  return planId.includes('v1');
};

export const isV2Subscriber = (planId: string) => {
  return planId.includes('v2');
};

export const isV3Subscriber = (planId: string) => {
  return planId.includes('v3');
};

/**
 *
 * @param {Currency} currencyCode
 * @param {boolean} isUSMedium
 * @return {Plans}
 */
export const getIndividualPlanCollectionByCurrencyCode = (
  currencyCode: Currency,
  isUSMedium: boolean = false,
): Plans => {
  let planCollection: Plans;

  switch (currencyCode) {
    case 'GBP':
      planCollection = individualPlansGB;
      break;

    case 'USD':
      planCollection = isUSMedium ? individualPlansUSDMedium : individualPlansUS;
      break;

    case 'EUR':
      planCollection = individualPlansEU;
      break;

    case 'AUD':
      planCollection = individualPlansAU;
      break;

    case 'CAD':
      planCollection = individualPlansCA;
      break;
  }

  return planCollection;
};
